import React, { useCallback } from 'react';
import { Box } from '@mui/material';

import amplitudeLog from '@client/amplitude';
import {
  AMP_PROJECT_EXPORT_BOVERKET_XML,
  AMP_PROJECT_EXPORT_SPREADSHEET,
  AMP_PROJECT_SAVE_AS_TEMPLATE,
} from '@shared/constants';
import { useExportSpreadsheet } from '@client/hooks/use-export-spreadsheet.hook';
import { useExportBoverketXml } from '@client/hooks/use-export-boverket-xml.hook';
import { useUserIsOwner } from '@client/hooks/use-user.hooks';
import { useErrorHandler } from '@client/hooks/use-error-handler.hooks';
import { useProjectRestriction } from '@client/store/project';
import { validateFiniteNumber } from '@shared/validation/number.validation';
import { Menu } from '@ui/menus/Menu';
import KebabButton from '@ui/menus/KebabMenu/components/KebabButton';

import { ProjectKebabMenu as ProjectKebabMenuProps } from './project-menu.model';
import { useEditProjectDialogStore } from '../../../EditProject/edit-project.store';
import { ProjectMenuContent } from './ProjectMenuContent';
import { useProjectAction } from '../use-project-action.hook';
import { mergeSx } from '@client/helpers/mui.helpers';

const kebabTriggerSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const;

export const ProjectKebabMenu: React.FC<ProjectKebabMenuProps> = ({
  projectId,
  projectName,
  locked,
  archived,
  updateProducts,
  onClose,
  ...props
}) => {
  if (typeof projectId !== 'number' || !isFinite(projectId)) {
    throw new Error('Invalid project id');
  }

  const restrictProject = useProjectRestriction();
  const exportSpreadsheet = useExportSpreadsheet();
  const exportBoverketXml = useExportBoverketXml();
  const errorHandler = useErrorHandler();
  const setEditProjectDialog = useEditProjectDialogStore((state) => state.set);

  const projectAction = useProjectAction(projectId);

  const isOwner = useUserIsOwner(projectId);

  const handleDuplicateProject = projectAction((validProjectId) => {
    setEditProjectDialog({
      type: 'project',
      mode: 'duplicate',
      id: validProjectId,
    });
  }, 'duplicate-project');

  const handleLockProject = useCallback(() => {
    restrictProject(validateFiniteNumber(projectId), projectName, {
      lock: true,
    });
  }, [projectId, projectName, restrictProject]);

  const handleUnlockProject = useCallback(() => {
    restrictProject(validateFiniteNumber(projectId), projectName, {
      lock: false,
    });
  }, [projectId, projectName, restrictProject]);

  const handleArchiveProject = projectAction((validProjectId) => {
    restrictProject(
      validProjectId,
      projectName,
      { archive: true, lock: false },
      true,
    );
  }, 'archive-project');

  const handleUnarchiveProject = projectAction(
    (validProjectId) =>
      restrictProject(validProjectId, projectName, { archive: false }, true),
    'unarchive-project',
  );

  const handleSaveAsTemplate = projectAction((validProjectId) => {
    amplitudeLog(AMP_PROJECT_SAVE_AS_TEMPLATE, {
      ProjectId: validProjectId,
    });
    setEditProjectDialog({
      type: 'template',
      mode: 'create',
      id: validProjectId,
    });
  }, 'create-template-project');

  const handleExportSpreadsheet = projectAction(async (validProjectId) => {
    amplitudeLog(AMP_PROJECT_EXPORT_SPREADSHEET, { ProjectId: validProjectId });
    return errorHandler(() => exportSpreadsheet(validProjectId));
  }, 'export-spreadsheet');

  const handleExportBoverketXml = projectAction(async (validProjectId) => {
    amplitudeLog(AMP_PROJECT_EXPORT_BOVERKET_XML, {
      ProjectId: validProjectId,
    });
    return errorHandler(() => exportBoverketXml(validProjectId));
    // Same pro gate as spreadsheet export until a dedicated paywall feature exists.
  }, 'export-spreadsheet');

  const editProject = useCallback(() => {
    setEditProjectDialog({ type: 'project', mode: 'edit', id: projectId });
  }, [projectId, setEditProjectDialog]);

  const { anchor, menuWidth, buttonProps, disabled, sx, onOpen } = props;
  const open = Boolean(anchor);

  const handleOpen = useCallback(
    (event: React.MouseEvent<Element>) => {
      onOpen?.(event.currentTarget);
    },
    [onOpen],
  );

  return (
    <>
      <Box sx={mergeSx(kebabTriggerSx, sx)}>
        <KebabButton
          {...buttonProps}
          disabled={disabled}
          onClick={handleOpen}
        />
      </Box>
      <Menu open={open} anchorEl={anchor} onClose={onClose} width={menuWidth}>
        <ProjectMenuContent
          projectId={String(projectId)}
          locked={locked}
          archived={archived}
          isOwner={isOwner}
          actions={{
            edit: editProject,
            duplicate: handleDuplicateProject,
            lock: handleLockProject,
            unlock: handleUnlockProject,
            archive: handleArchiveProject,
            unarchive: handleUnarchiveProject,
            template: handleSaveAsTemplate,
            exportSpreadsheet: handleExportSpreadsheet,
            exportBoverketXml: handleExportBoverketXml,
            updateProducts,
          }}
        />
      </Menu>
    </>
  );
};
